/**
 * DoubleTake queue buckets (ENG-1756).
 *
 * "Filtered Queue" is the working queue: everything except the alerts
 * DoubleTake thinks are false positives, so unsorted alerts stay visible.
 * "All" adds the likely FPs back. The two verdict buckets show one class each.
 */
export type VlmFilterType =
  | "Filtered Queue"
  | "All"
  | "Likely TP"
  | "Likely FP"
  | "Withheld";

/** Tile copy, in the order the tiles are rendered. */
export const VLM_FILTER_ORDER: VlmFilterType[] = [
  "Filtered Queue",
  "All",
  "Likely TP",
  "Likely FP",
  "Withheld",
];

export const VLM_FILTER_LABELS: Record<VlmFilterType, string> = {
  "Filtered Queue": "Filtered Queue",
  All: "All",
  "Likely TP": "Likely TP",
  "Likely FP": "Likely FP",
  Withheld: "Withheld",
};

/**
 * ENG-1599: the connector's DoubleTake receiver gate stamps
 * `dispatch_suppressed_by = "doubletake"` on an alert it recorded but did NOT
 * deliver to Immix/Patriot. Server truth, like `dispatch_muted` (ENG-1309):
 * the alert is still here for operators to review, the monitoring center never
 * saw it. A withheld alert later marked a true threat is the miss signal.
 */
export const isAlertWithheldFromReceiver = (row: any): boolean =>
  typeof row?.dispatch_suppressed_by === "string" &&
  row.dispatch_suppressed_by.trim().toLowerCase() === "doubletake";

type VlmClassification = "filtered" | "passed" | null;

const normalizeVerdict = (verdict: unknown): string | null => {
  if (typeof verdict !== "string") return null;
  return verdict.trim().toLowerCase().replace(/\s+/g, " ");
};

const getVlmClassification = (row: any): VlmClassification => {
  if (!row) return null;

  // Prefer backend verdict when present (including explicit nulls).
  if ("vlm_verdict" in row) {
    const normalizedVerdict = normalizeVerdict(row.vlm_verdict);
    if (normalizedVerdict === "passed") {
      return "passed";
    }
    if (normalizedVerdict === "suppressed") {
      return "filtered";
    }
    return null;
  }

  if (typeof row.vlm_filtered === "boolean") {
    return row.vlm_filtered ? "filtered" : "passed";
  }

  if (typeof row.vlm_passed === "boolean") {
    return row.vlm_passed ? "passed" : "filtered";
  }

  if (row.vlm_result === "filtered" || row.vlm_result === "passed") {
    return row.vlm_result;
  }

  return null;
};

/**
 * The verifier's classification for a row, or null when it never produced one
 * ("unsorted"). Drives the per-row Likely TP / Likely FP indicators.
 */
export const getAlertVlmVerdict = (row: any): VlmClassification =>
  getVlmClassification(row);

export const vlmVerdictSortRank = (row: any): number => {
  const verdict = getAlertVlmVerdict(row);
  if (verdict === "passed") return 0;
  if (verdict === "filtered") return 1;
  return 2;
};

export const isAlertVlmFiltered = (row: any): boolean => {
  return getVlmClassification(row) === "filtered";
};

export const hasAlertVlmClassification = (row: any): boolean => {
  return getVlmClassification(row) !== null;
};

export const applyVlmFilter = (
  rows: any[],
  vlmFilterType: VlmFilterType = "All"
): any[] => {
  if (vlmFilterType === "Filtered Queue") {
    // Everything DoubleTake did not call a false positive — unsorted included.
    return rows.filter((row) => getVlmClassification(row) !== "filtered");
  }
  if (vlmFilterType === "Likely FP") {
    return rows.filter((row) => getVlmClassification(row) === "filtered");
  }
  if (vlmFilterType === "Likely TP") {
    return rows.filter((row) => getVlmClassification(row) === "passed");
  }
  if (vlmFilterType === "Withheld") {
    return rows.filter(isAlertWithheldFromReceiver);
  }
  return rows;
};

export const getVlmQueueCounts = (rows: any[]) => {
  const vlmFiltered = rows.filter(
    (row) => getVlmClassification(row) === "filtered"
  ).length;
  const passed = rows.filter((row) => getVlmClassification(row) === "passed").length;
  const unresolved = rows.filter((row) => !row?.detection_type).length;
  const withheld = rows.filter(isAlertWithheldFromReceiver).length;
  return {
    unresolved,
    passed,
    vlmFiltered,
    withheld,
    // The working queue: everything except the likely false positives.
    filteredQueue: rows.length - vlmFiltered,
    total: rows.length,
  };
};

/** Count shown on each tile, so the tiles and the filter never disagree. */
export const getVlmFilterCount = (
  counts: ReturnType<typeof getVlmQueueCounts>,
  filter: VlmFilterType
): number => {
  if (filter === "Filtered Queue") return counts.filteredQueue;
  if (filter === "Likely TP") return counts.passed;
  if (filter === "Likely FP") return counts.vlmFiltered;
  if (filter === "Withheld") return counts.withheld;
  return counts.total;
};

// Detection classes DoubleTake evaluates today. Mirrors _PERSON_CLASSES and
// _VEHICLE_CLASSES in ds-smart-alert-supervisor/lambda/handler.py (vehicle DT
// went to production in AI-1005); every other metric (loiterer, gun, ...)
// bypasses the verifier.
const DOUBLETAKE_SUPPORTED_LABELS = new Set([
  "person",
  "intruder",
  "vehicle",
  "car",
  "truck",
  "bus",
  "machinery",
]);

/**
 * False when the alert's metric is one DoubleTake does not evaluate. An alert
 * with no label at all is treated as supported so we never claim
 * unavailability without evidence.
 */
export const isDoubleTakeSupportedMetric = (row: any): boolean => {
  const label = row?.alert_labels;
  const first = Array.isArray(label) ? label[0] : label;
  if (typeof first !== "string" || first.trim() === "") return true;
  return DOUBLETAKE_SUPPORTED_LABELS.has(first.trim().toLowerCase());
};

/**
 * True when the alerts API says DoubleTake is off for this alert's site or
 * camera (`vlm_site_enabled` / `vlm_camera_enabled`, actuate_monitoring_api,
 * ENG-1756). A missing flag is "unknown", not "off".
 */
export const isDoubleTakeDisabledForAlert = (row: any): boolean => {
  return row?.vlm_site_enabled === false || row?.vlm_camera_enabled === false;
};

/**
 * How long a no-verdict alert can be considered "pending". The verifier runs
 * async and writes its verdict within seconds; the queue refreshes every ~30 s.
 * Anything older with no verdict was never going to get one.
 */
export const VLM_PENDING_WINDOW_MS = 10 * 60 * 1000;

const getAlertAgeMs = (row: any, now: number): number | null => {
  const raw = row?.approx_capture_timestamp;
  const seconds = typeof raw === "number" ? raw : parseFloat(raw);
  if (!Number.isFinite(seconds)) return null;
  return now - seconds * 1000;
};

/**
 * Banner copy for the selected alert. Verdicts come from the verifier
 * (`vlm_verdict` on WindowIdsV2, surfaced by actuate_monitoring_api):
 *   passed             → likely true positive
 *   suppressed         → likely false positive
 *   not_evaluated /
 *   unable_to_evaluate → the alert never went through the filter, or the
 *                        model could not classify it → "could not be sorted".
 *                        A written verdict wins over the label check: the
 *                        verifier did see this alert (ENG-2735).
 *   (no verdict on a metric DoubleTake does not evaluate → "unavailable for
 *   this alert metric")
 *   null / missing     → no verdict written. If the API flags say DoubleTake
 *                        is off for the site/camera → "disabled on this
 *                        camera". If the alert is still inside the pending
 *                        window → "verdict still pending". Otherwise (older,
 *                        or no timestamp) → "verdict unavailable".
 */
export const getVlmStatusMessage = (row: any, now: number = Date.now()): string => {
  const classification = getVlmClassification(row);
  if (classification === "filtered") {
    if (isAlertWithheldFromReceiver(row)) {
      return "DOUBLETAKE identified this as a likely false positive — not sent to the monitoring center";
    }
    return "DOUBLETAKE identified this as a likely false positive";
  }
  if (classification === "passed") {
    return "DOUBLETAKE identified this as a likely true positive";
  }

  const verdict = normalizeVerdict(row?.vlm_verdict);
  if (verdict) {
    return "Alert could not be sorted by DOUBLETAKE";
  }
  if (!isDoubleTakeSupportedMetric(row)) {
    return "DOUBLETAKE unavailable for this alert metric";
  }
  if (isDoubleTakeDisabledForAlert(row)) {
    return "DOUBLETAKE disabled on this camera";
  }
  const ageMs = getAlertAgeMs(row, now);
  if (ageMs !== null && ageMs <= VLM_PENDING_WINDOW_MS) {
    return "DOUBLETAKE verdict still pending";
  }
  return "DOUBLETAKE verdict unavailable for this alert";
};

/**
 * What to do when the selected alert is hidden by the DoubleTake tile filter
 * (it is in the list before that filter, not after). Only meaningful with
 * `showVlmFilters` on and a filter other than "All".
 *
 * - `select-first`: nobody chose this alert on purpose - the page just loaded
 *   and picked the newest one, the flag flipped on, a refresh or auto-advance
 *   moved the selection, or the operator clicked a tile and asked for a
 *   different bucket. Showing an alert the table does not contain, with both
 *   arrows dead, helps nobody: jump to the first alert in the queue.
 * - `keep-and-allow-forward`: the selection and the filter are unchanged, so the
 *   data moved under the operator - typically a verdict arrived and dropped the
 *   alert they are reviewing out of the queue. Do NOT yank the clip away
 *   mid-review; keep it, and let "next" carry on into the queue.
 * - `keep`: nothing in the queue to move to.
 */
export type SelectionOutsideVlmFilterAction =
  | "select-first"
  | "keep-and-allow-forward"
  | "keep";

export const resolveSelectionOutsideVlmFilter = ({
  selectionChanged,
  filterChanged,
  filteredCount,
}: {
  selectionChanged: boolean;
  filterChanged: boolean;
  filteredCount: number;
}): SelectionOutsideVlmFilterAction => {
  if (filteredCount <= 0) return "keep";
  return selectionChanged || filterChanged
    ? "select-first"
    : "keep-and-allow-forward";
};

/**
 * Whether the alerts in view (the group/site/camera filter's result) include a
 * DoubleTake camera, so the DoubleTake UI (queue tiles, DT Verdict column and
 * chips, status banner) is only drawn when it has something to act on.
 *
 * True when any row's camera or site flag is on, or any row carries a
 * verdict or the receiver gate's withheld stamp (DoubleTake evidently ran).
 * False when the API flagged every row
 * off and nothing was sorted. With no flags on any row at all (older API, or
 * the lookup failed and the flags were omitted) the answer is unknown, and
 * unknown is not "off": the UI stays as it was. An empty list has nothing to
 * filter, so it is false.
 */
export const hasDoubleTakeInAlerts = (rows: any[] | null | undefined): boolean => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  let sawFlag = false;
  for (const row of rows) {
    if (row?.vlm_camera_enabled === true || row?.vlm_site_enabled === true) {
      return true;
    }
    if (getVlmClassification(row) !== null) return true;
    // ENG-1599/ENG-2272: the receiver gate's stamp is DoubleTake evidence too,
    // so the Withheld tile is never hidden while a withheld badge is showing.
    if (isAlertWithheldFromReceiver(row)) return true;
    if (
      typeof row?.vlm_camera_enabled === "boolean" ||
      typeof row?.vlm_site_enabled === "boolean"
    ) {
      sawFlag = true;
    }
  }
  return !sawFlag;
};
